import { X } from "lucide-react";
import React, { useEffect, useMemo } from "react";

import { IgDivider, IgTextField, IgTypography } from "@intelligems/ig-theme";
import {
  StripeApiGetAllPricesByProductIdResponseBodyType,
  StripeApiSubscriptionItemPairDto,
} from "@intelligems/ig-types";
import { ensureNumber } from "@intelligems/ig-utils";
import { Grid, IconButton, InputAdornment, useTheme } from "@mui/material";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { ChoosePriceField } from "@src/features/admin/components/Fields/ChoosePriceField";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";
import { PriceTierTable } from "@src/features/admin/pages/overview/components/Tables/PriceTierTable/PriceTierTable";

interface PriceLineProps {
  index: number;
  item: StripeApiSubscriptionItemPairDto;
  items: StripeApiSubscriptionItemPairDto[];
  allData: StripeApiSubscriptionItemPairDto[];
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: React.Dispatch<CreateSubscriptionReducerAction>;
}

const isPausePlan = (price: any) => {
  if (price) {
    return price?.name?.toLowerCase().includes("pause");
  }

  return false;
};

export const PriceLine = ({
  index,
  item,
  items,
  allData,
  payload,
  dispatchPayload,
}: PriceLineProps) => {
  const theme = useTheme();
  const prices = useStripeApi<StripeApiGetAllPricesByProductIdResponseBodyType>(
    {
      action: "getPricesByProductId",
      productId: item.product.id,
      orgs: payload.orgs,
    }
  );

  const data = useMemo(() => {
    if (!prices.isSuccess) {
      return undefined;
    }

    if (items.length <= 1) {
      return prices.data.prices;
    }

    const firstItem = items[0];
    return prices.data.prices
      .filter((price) => {
        return (
          price.active &&
          price.interval === firstItem.price?.interval &&
          !items.some((i) => i.price?.id === price.id)
        );
      })
      .sort();
  }, [prices.isSuccess, prices.data, items]);

  useEffect(() => {
    // dispatches setPriceQuantity for pause plan to set quantity to 1
    if (item.price && isPausePlan(item.price)) {
      dispatchPayload({
        type: "setPriceQuantity",
        value: 1,
        priceId: item.price.id,
      });
    }
  }, [item]);
  return (
    <Grid item mt={1}>
      <Grid container item direction={"column"}>
        {index > 0 ? (
          <Grid item flex={1}>
            <IgDivider spacing={"l"} />
          </Grid>
        ) : null}
        <Grid
          container
          item
          flex={1}
          justifyContent={"space-between"}
          alignItems={"center"}
          columnSpacing={1}
          rowSpacing={2}
          flexWrap={"nowrap"}
        >
          <Grid item flex={1}>
            <ChoosePriceField
              prices={data}
              item={item}
              payload={payload}
              dispatchPayload={dispatchPayload}
            />
          </Grid>
          <Grid item sx={{ width: "200px" }}>
            <IgTextField
              label={"Quantity"}
              type={"number"}
              disabled={
                item.isReadOnly || !item.price || isPausePlan(item.price)
              }
              value={isPausePlan(item.price) ? 1 : (item.quantity ?? "")}
              onChange={(e) => {
                if (item.price) {
                  dispatchPayload({
                    type: "setPriceQuantity",
                    value: ensureNumber(e.target.value),
                    priceId: item.price.id,
                  });
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {item.price?.meterId ? "orders / month" : "quantity"}{" "}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <IconButton
              onClick={() => {
                dispatchPayload({ type: "removeItemByIdx", idx: item.idx });
              }}
            >
              <X size={16} color={theme.igIconColors.error} />
            </IconButton>
          </Grid>
        </Grid>
        {item.price?.meterId ? (
          <Grid item sx={{ marginTop: "10px" }}>
            <IgTypography
              variant={"body2"}
              sx={{ color: theme.palette.text.tonal.warning }}
            >
              This pricing model is metered, meaning Intelligems will send ax
              usage event to Stripe at the beginning of each subscription period
              to calculate the customer’s charge. Note: Automatic subscription
              scaling based on monthly usage is not supported at this time.
              Please provide a default Shopify order quantity to be used.
            </IgTypography>
          </Grid>
        ) : null}
        {item.price && item.price.tiers?.length ? (
          <Grid item sx={{ width: "100%", marginTop: "10px" }}>
            <PriceTierTable
              coupon={payload.phases[payload.phaseIndex].coupon}
              tiers={item.price.tiers}
              quantity={item.quantity || 1}
            />
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};
